import { ChangeDetectionStrategy, Component, Inject, Injector, Type } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DialogResult } from 'src/app/models/dialog-result';
import { IMasterSuite } from 'src/app/models/mastersuites/mastersuite';
import { IProduct } from 'src/app/models/products/product';
import {
  IProductRef,
  ITouchMapping,
  ITransferDestination,
  ITransferPrepareResult,
  ITransferSource,
} from 'src/app/models/products/actions/settings/transferProductSettings';
import { MapAssetsStepComponent, MapAssetsStepResult } from './map-assets-step/map-assets-step.component';
import { MapTouchesStepComponent, MapTouchesStepResult } from './map-touches-step/map-touches-step.component';
import {
  SelectMasterSuitesStepComponent,
  SelectMasterSuitesStepResult,
  WizardEnhancementVariantRef,
} from './select-master-suites-step/select-master-suites-step.component';
import {
  SelectProductsStepComponent,
  SelectProductsStepResult,
} from './select-products-step/select-products-step.component';

export interface TransferProductSettingsWizardData {
  readonly masterSuites: IMasterSuite[];
  readonly products: IProduct[];
}

@Component({
  selector: 'app-transfer-product-settings-wizard',
  templateUrl: './transfer-product-settings-wizard.component.html',
  styleUrls: ['./transfer-product-settings-wizard.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class TransferProductSettingsWizardComponent {
  readonly steps = ['Select Master Suites', 'Select Products', 'Map Touches', 'Map Assets'];
  readonly stepComponents: Type<unknown>[] = [
    SelectMasterSuitesStepComponent,
    SelectProductsStepComponent,
    MapTouchesStepComponent,
    MapAssetsStepComponent,
  ];

  currentStep = 1;
  completedSteps: number[] = [];

  private source?: ITransferSource;
  private sourceEnhancementVariant?: WizardEnhancementVariantRef;
  private destination?: ITransferDestination;
  private destinationEnhancementVariant?: WizardEnhancementVariantRef;
  private products?: IProductRef[];
  private touchMappings?: ITouchMapping[];
  private prepareResult?: ITransferPrepareResult;

  private readonly stepInjectorCache = new Map<number, Injector>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly data: TransferProductSettingsWizardData,
    private readonly dialogRef: MatDialogRef<TransferProductSettingsWizardComponent>,
    private readonly parentInjector: Injector,
  ) {}

  get currentStepComponent(): Type<unknown> {
    return this.stepComponents[this.currentStep - 1];
  }

  get currentStepInjector(): Injector {
    const cached = this.stepInjectorCache.get(this.currentStep);
    if (cached) return cached;
    const injector = this.createStepInjector(this.currentStep);
    this.stepInjectorCache.set(this.currentStep, injector);
    return injector;
  }

  private createStepInjector(step: number): Injector {
    return Injector.create({
      parent: this.parentInjector,
      providers: [
        { provide: MAT_DIALOG_DATA, useValue: this.buildStepData(step) },
        { provide: MatDialogRef, useValue: this.makeStubDialogRef(step) },
      ],
    });
  }

  private sourceMasterSuites(): IMasterSuite[] {
    const inUseIds = new Set(this.data.products.map((product) => product.masterSuiteId));
    return this.data.masterSuites.filter((masterSuite) => inUseIds.has(masterSuite.id ?? null));
  }

  private invalidateInjectorCacheFrom(step: number): void {
    [...this.stepInjectorCache.keys()]
      .filter((cachedStep) => cachedStep >= step)
      .forEach((cachedStep) => this.stepInjectorCache.delete(cachedStep));
  }

  goToStep(step: number): void {
    if (step >= 1 && step <= this.steps.length && this.completedSteps.includes(step - 1) || step === 1) {
      this.currentStep = step;
    }
  }

  private buildStepData(step: number): unknown {
    if (step === 1) {
      return {
        masterSuites: this.data.masterSuites,
        sourceMasterSuites: this.sourceMasterSuites(),
        products: this.data.products,
        initialSourceId: this.source?.masterSuite.id,
        initialSourceEnhancementVariantId: this.source?.enhancementVariant.id,
        initialDestinationId: this.destination?.masterSuite.id,
        initialVariantId: this.destination?.settingsVariant.id,
        initialEnhancementVariantId: this.destination?.enhancementVariant.id,
      };
    }
    if (step === 2) {
      return {
        products: this.data.products,
        masterSuiteSourceId: this.source?.masterSuite.id,
        sourceEnhancementVariantId: this.source?.enhancementVariant.id,
      };
    }
    if (step === 3) {
      return {
        source: this.source,
        destination: this.destination,
        products: this.products,
      };
    }
    return {
      source: this.source,
      destination: this.destination,
      sourceEnhancementVariant: this.sourceEnhancementVariant,
      destinationEnhancementVariant: this.destinationEnhancementVariant,
      products: this.products,
      touchMappings: this.touchMappings,
      prepareResult: this.prepareResult,
    };
  }

  private makeStubDialogRef(step: number): MatDialogRef<unknown> {
    const noop = { subscribe: () => ({ unsubscribe: () => undefined }) };
    const stub = {
      close: (result: unknown) => this.onEmbeddedClose(step, result),
      afterClosed: () => noop,
      afterOpened: () => noop,
      backdropClick: () => noop,
      keydownEvents: () => noop,
      addPanelClass: () => stub,
      removePanelClass: () => stub,
      updatePosition: () => stub,
      updateSize: () => stub,
      disableClose: false,
    };
    return stub as unknown as MatDialogRef<unknown>;
  }

  private onEmbeddedClose(step: number, result: unknown): void {
    if (result === DialogResult.Cancel || result === undefined) {
      this.dialogRef.close();
      return;
    }

    if (step === 1) {
      const stepResult = result as SelectMasterSuitesStepResult;
      this.source = {
        masterSuite: { id: stepResult.sourceMasterSuiteId },
        enhancementVariant: { id: stepResult.sourceEnhancementVariant.id },
      };
      this.sourceEnhancementVariant = stepResult.sourceEnhancementVariant;
      this.destination = {
        masterSuite: { id: stepResult.destinationMasterSuiteId },
        settingsVariant: { id: stepResult.destinationSettingsVariantId },
        enhancementVariant: { id: stepResult.destinationEnhancementVariant.id },
      };
      this.destinationEnhancementVariant = stepResult.destinationEnhancementVariant;
    } else if (step === 2) {
      this.products = (result as SelectProductsStepResult).products;
    } else if (step === 3) {
      const stepResult = result as MapTouchesStepResult;
      this.touchMappings = stepResult.touchMappings;
      this.prepareResult = stepResult.prepareResult;
    } else if (step === 4) {
      const stepResult = result as MapAssetsStepResult;
      this.dialogRef.close(stepResult.confirmed);
      return;
    }

    if (!this.completedSteps.includes(step)) {
      this.completedSteps = [...this.completedSteps, step];
    }
    if (step < this.steps.length) {
      this.invalidateInjectorCacheFrom(step);
      this.currentStep = step + 1;
    }
  }
}
